import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef,
  inject,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ReactiveFormsModule,
  FormControl,
  Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import { I18nService } from '../../../core/services/i18n.service';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { DomainFeedback } from '../../../core/models/domain-feedback.model';
import {
  isValidDomainInput,
  isPresencaDigitalDomainSuffix,
} from '../../validators/domain.validator';


@Component({
  selector: 'umkt-verify-domain',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, I18nPipe],
  templateUrl: './verify-domain.component.html',
  styleUrls: ['./verify-domain.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VerifyDomainComponent),
      multi: true,
    },
  ],
})
export class VerifyDomainComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() domainSuffix?: string;
  @Input() label: string = 'Nome do novo domínio';
  @Input() isValid: boolean | null = null;
  @Input() borderColor: string = '#e5e7eb';
  @Input() borderRadius: string = '0.375rem';
  @Input() customErrorMessage?: string;

  @Input() isLoading: boolean = false;
  @Input() feedback?: DomainFeedback | null;

  @Output() domainChange = new EventEmitter<string>();

  domainControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(253),
  ]);

  domainValue: string = '';
  uniqueId: string = '';
  isFocused: boolean = false;
  formatError: string = '';

  private i18nService = inject(I18nService);
  private cdr = inject(ChangeDetectorRef);
  private langSubscription?: Subscription;

  private onChange = (_value: string) => {
    // Implementação vazia - será sobrescrita pelo registerOnChange
  };
  private onTouched = () => {
    // Implementação vazia - será sobrescrita pelo registerOnTouched
  };
  private _disabled = false;

  ngOnInit() {
    this.uniqueId = 'domain-input-' + crypto.randomUUID();

    this.domainControl.valueChanges.subscribe(value => {
      this.domainValue = value || '';

      if (!this.domainValue) {
        this.resetToInitialState();
      } else {
        this.checkFormatDuringTyping();
      }

      this.onChange(this.domainValue);
      this.domainChange.emit(this.domainValue);
    });

    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      if (this.formatError) {
        this.checkFormatDuringTyping();
      }
      
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  resetToInitialState() {
    this.formatError = '';
  }

  blockAtSymbol(event: KeyboardEvent): void {
    if (event.key === '@') {
      event.preventDefault();
    }
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value.length > 253) {
      this.domainControl.setValue(value.substring(0, 253));
      return;
    }

    this.domainValue = value;

    if (!value) {
      this.resetToInitialState();
    }
  }

  getBorder(): string {
    return `1px solid ${this.borderColor}`;
  }

  getBorderRadius(): string {
    return this.borderRadius;
  }

  checkFormatDuringTyping() {
    if (!this.domainValue) {
      this.formatError = '';
      return;
    }

    if (!this.isValidDomainFormat(this.domainValue)) {
      if (isPresencaDigitalDomainSuffix(this.domainSuffix)) {
        this.formatError = this.i18nService.instant(
          'components.verifyDomain.errorFormatPresencaDigital'
        );
      } else if (this.domainSuffix) {
        this.formatError = this.i18nService.instant(
          'components.verifyDomain.errorFormatSuffix'
        );
      } else {
        this.formatError = this.i18nService.instant(
          'components.verifyDomain.errorFormat'
        );
      }
    } else {
      this.formatError = '';
    }
  }

  private isValidDomainFormat(domain: string): boolean {
    return isValidDomainInput(domain, this.domainSuffix);
  }

  clearDomain() {
    this.domainControl.setValue('');
    this.resetToInitialState();
  }

  getFullDomain(): string {
    if (!this.domainSuffix) {
      return this.domainValue || '';
    }
    return this.domainValue ? this.domainValue + this.domainSuffix : '';
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    this.isFocused = false;
  }

  getErrorMessage(): string {
    if (this.customErrorMessage) {
      return this.customErrorMessage;
    }

    if (this.formatError) {
      return this.formatError;
    }

    if (this.isValid === false) {
      return this.i18nService.instant('components.verifyDomain.errorUnavailable');
    }

    return '';
  }

  hasFormatError(): boolean {
    return !!this.formatError;
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.domainValue = value || '';
    this.domainControl.setValue(this.domainValue, { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.domainControl.disable();
    } else {
      this.domainControl.enable();
    }
  }

  get titleKey(): string {
    if (this.currentStatus === 'loading') {
      return 'components.verifyDomain.loadingTitle';
    }
    if (this.currentStatus === 'success') {
      return 'components.verifyDomain.successTitle';
    }
    return 'components.verifyDomain.warningTitle';
  }

  get descriptionKey(): string | null {
    if (this.currentStatus === 'loading') {
      return 'components.verifyDomain.loadingDescription';
    }

    return null;
  }

  get descriptionParams(): any {
    return {
      domain: this.getFullDomain()
    };
  }


  get currentStatus(): 'success' | 'warning' | 'loading' | 'error' | 'none' {
    if (this.isLoading) return 'loading';

    if (this.feedback?.type) {
      return this.feedback.type;
    }

    if (this.formatError || this.isValid === false) return 'warning';
    if (this.isValid === true && this.domainValue) return 'success';

    return 'none';
  }

  getInputClass(): string {
    const status = this.currentStatus;
    return status !== 'none' ? `domain-input ${status}` : 'domain-input';
  }

  get shouldShowFeedback(): boolean {
    if (this.currentStatus === 'loading') {
      return true;
    }

    if (this.feedback?.title || this.feedback?.description) {
      return true;
    }

    if (this.currentStatus === 'warning' && this.getErrorMessage()) {
      return true;
    }

    return false;
  }

  get title(): string {
    if (this.feedback?.title) {
      return this.feedback.title;
    }

    if (this.currentStatus === 'loading') {
      return this.i18nService.instant('components.verifyDomain.loadingTitle');
    }

    return this.i18nService.instant(this.titleKey);
  }

  get description(): string {
    if (this.feedback?.description) {
      return this.feedback.description;
    }

    if (this.currentStatus === 'loading') {
      return this.i18nService.instant(
        'components.verifyDomain.loadingDescription',
        { domain: this.getFullDomain() }
      );
    }

    if (this.currentStatus === 'warning') {
      return this.getErrorMessage();
    }

    if (this.descriptionKey) {
      return this.i18nService.instant(this.descriptionKey, this.descriptionParams);
    }

    return '';
  }

  get feedbackIcon(): string {
    if (this.currentStatus === 'loading') {
      return 'mdn-Icon-relogio';
    }

    if (this.currentStatus === 'success') {
      if (this.feedback?.isDomainFree) {
        return 'mdn-Icon-presente';
      }
      return 'mdn-Icon-check';
    }

    if (this.currentStatus === 'warning') {
      return 'mdn-Icon-triangulo-exclamacao';
    }

    if (this.currentStatus === 'error') {
      return 'mdn-Icon-circulo-fechar';
    }

    return '';
  }

}
